import React, { useState, useEffect } from 'react';
import { ChartBarIcon, DocumentArrowDownIcon, CalendarIcon, FunnelIcon } from '@heroicons/react/24/outline';
import reportService from '../../services/reportService';
import { toast } from 'react-toastify';



const Reports = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    agencyId: '',
    carrierId: '',
    status: '',
    vehicleTypeId: '',
    fromRegionId: '',
    toRegionId: ''
  });
  const [exportFormat, setExportFormat] = useState('pdf');

  // Tarih filtrelerini varsayılan olarak ayarla (son 30 gün)
  useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);

    setFilters(prev => ({
      ...prev,
      startDate: thirtyDaysAgo.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0]
    }));
  }, []);

  // Rapor verilerini getir
  const fetchReportData = async () => {
    if (!filters.startDate || !filters.endDate) {
      toast.error('Başlangıç ve bitiş tarihleri gereklidir');
      return;
    }

    setLoading(true);
    try {
      let response;

      switch (activeTab) {
        case 'reservations':
          response = await reportService.getReservationReport(filters);
          break;
        case 'operations':
          response = await reportService.getOperationReport(filters);
          break;
        case 'financial':
          response = await reportService.getFinancialReport(filters);
          break;
        case 'agencies':
          response = await reportService.getAgencyReport(filters);
          break;
        case 'carriers':
          response = await reportService.getCarrierReport(filters);
          break;
        case 'vehicles':
          response = await reportService.getVehicleReport(filters);
          break;
        case 'regions':
          response = await reportService.getRegionReport(filters);
          break;
        case 'dashboard':
        default:
          response = await reportService.getDashboardReport(filters);
          break;
      }

      console.log('Rapor verileri geldi:', response.data);
      setReportData(response.data.data);
    } catch (error) {
      toast.error('Rapor verileri yüklenemedi');
      console.error('Rapor hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  // Raporu dışa aktar
  const exportReport = async () => {
    if (!reportData) {
      toast.error('Önce rapor verilerini yükleyin');
      return;
    }

    setLoading(true);
    try {
      const response = await reportService.exportReport({
        reportType: activeTab,
        format: exportFormat,
        filters
      });

      // Dosyayı indir
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${activeTab}-report.${exportFormat === 'excel' ? 'xlsx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('Rapor başarıyla dışa aktarıldı');
    } catch (error) {
      toast.error('Rapor dışa aktarılamadı');
      console.error('Dışa aktarma hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filtre değişikliklerini işle
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Raporu yenile
  const refreshReport = () => {
    fetchReportData();
  };

  // Rapor tab'leri
  const reportTabs = [
    { id: 'dashboard', name: 'Dashboard Özeti', icon: ChartBarIcon },
    { id: 'reservations', name: 'Rezervasyon Raporu', icon: DocumentArrowDownIcon },
    { id: 'operations', name: 'Transfer Operasyon Raporu', icon: DocumentArrowDownIcon },
    { id: 'financial', name: 'Finansal Rapor', icon: DocumentArrowDownIcon },
    { id: 'agencies', name: 'Acente Performansı', icon: DocumentArrowDownIcon },
    { id: 'carriers', name: 'Taşıyıcı Performansı', icon: DocumentArrowDownIcon },
    { id: 'vehicles', name: 'Araç Kullanım Raporu', icon: DocumentArrowDownIcon },
    { id: 'regions', name: 'Bölge Raporu', icon: DocumentArrowDownIcon }
  ];

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Raporlar</h1>
        <p className="text-gray-600">Sistem verilerini analiz edin ve raporlar oluşturun</p>
      </div>

      {/* Rapor Tabları */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {reportTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm ${activeTab === tab.id
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              <tab.icon
                className={`-ml-0.5 mr-2 h-5 w-5 ${activeTab === tab.id ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'
                  }`}
                aria-hidden="true"
              />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Filtreler */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="flex items-center mb-4">
          <FunnelIcon className="h-5 w-5 text-gray-400 mr-2" />
          <h2 className="text-lg font-medium text-gray-900">Filtreler</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Başlangıç Tarihi</label>
            <input
              type="date"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bitiş Tarihi</label>
            <input
              type="date"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Durum</label>
            <select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              <option value="">Tümü</option>
              <option value="pending">Bekliyor</option>
              <option value="confirmed">Onaylandı</option>
              <option value="completed">Tamamlandı</option>
              <option value="cancelled">İptal Edildi</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Dışa Aktarma Formatı</label>
            <select
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              <option value="pdf">PDF</option>
              <option value="excel">Excel</option>
            </select>
          </div>
        </div>

        <div className="mt-4 flex space-x-3">
          <button
            onClick={refreshReport}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Yükleniyor...
              </>
            ) : (
              'Raporu Getir'
            )}
          </button>

          <button
            onClick={exportReport}
            disabled={loading || !reportData}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <DocumentArrowDownIcon className="-ml-1 mr-2 h-5 w-5 text-gray-400" />
            Dışa Aktar
          </button>
        </div>
      </div>

      {/* Rapor İçeriği */}
      <div className="bg-white shadow rounded-lg p-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
          </div>
        ) : reportData ? (
          <div>
            {/* Dashboard Özeti */}
            {activeTab === 'dashboard' && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Dashboard Özeti</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-blue-900">Toplam Rezervasyon</h3>
                    <p className="text-3xl font-bold text-blue-700">{reportData.reservations?.total || 0}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-green-900">Toplam Transfer</h3>
                    <p className="text-3xl font-bold text-green-700">{reportData.transfers?.total || 0}</p>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-yellow-900">Transfer Tamamlanma</h3>
                    <p className="text-3xl font-bold text-yellow-700">{reportData.transfers?.completionRate || '0%'}</p>
                  </div>
                </div>

                {/* Diğer dashboard verileri */}
                {reportData.topAgencies && reportData.topAgencies.length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-3">En Aktif Acenteler</h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      {reportData.topAgencies.map((agency, index) => (
                        <div key={agency.id} className="flex justify-between items-center py-2 border-b border-gray-200 last:border-b-0">
                          <span className="font-medium">{index + 1}. {agency.name}</span>
                          <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">
                            {agency.reservationCount} rezervasyon
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {reportData.topCarriers && reportData.topCarriers.length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-3">En Aktif Taşıyıcılar</h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      {reportData.topCarriers.map((carrier, index) => (
                        <div key={carrier.id} className="flex justify-between items-center py-2 border-b border-gray-200 last:border-b-0">
                          <span className="font-medium">{index + 1}. {carrier.name}</span>
                          <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded">
                            {carrier.transferCount} transfer
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Yaklaşan transferler */}
                {reportData.upcomingTransfers && reportData.upcomingTransfers.length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-3">Yaklaşan Transferler</h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      {reportData.upcomingTransfers.map((transfer) => (
                        <div key={transfer.id} className="flex justify-between items-center py-2 border-b border-gray-200 last:border-b-0">
                          <div>
                            <div className="font-medium">{transfer.reservationNumber}</div>
                            <div className="text-sm text-gray-500">{transfer.transferDate} {transfer.transferTime}</div>
                            <div className="text-sm text-gray-500">{transfer.fromRegion} → {transfer.toRegion}</div>
                          </div>
                          <div className="text-right">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${transfer.status === 'completed' ? 'bg-green-100 text-green-800' :
                              transfer.status === 'passenger_on_board' ? 'bg-purple-100 text-purple-800' :
                                transfer.status === 'arrived_at_pickup' ? 'bg-orange-100 text-orange-800' :
                                  transfer.status === 'on_the_way_to_pickup' ? 'bg-yellow-100 text-yellow-800' :
                                    transfer.status === 'vehicle_assigned' ? 'bg-indigo-100 text-indigo-800' :
                                      transfer.status === 'confirmed' ? 'bg-blue-100 text-blue-800' :
                                        transfer.status === 'pending' ? 'bg-gray-100 text-gray-800' :
                                          transfer.status === 'cancelled' || transfer.status === 'cancelled_by_customer' || transfer.status === 'cancelled_by_operator' || transfer.status === 'cancelled_by_driver' ? 'bg-red-100 text-red-800' :
                                            transfer.status === 'no_show' ? 'bg-orange-100 text-orange-800' :
                                              'bg-red-100 text-red-800'
                              }`}>
                              {transfer.status === 'completed' ? 'Tamamlandı' :
                                transfer.status === 'passenger_on_board' ? 'Yolcu Alındı' :
                                  transfer.status === 'arrived_at_pickup' ? 'Alış Noktasında' :
                                    transfer.status === 'on_the_way_to_pickup' ? 'Yolda' :
                                      transfer.status === 'vehicle_assigned' ? 'Araç Atandı' :
                                        transfer.status === 'confirmed' ? 'Onaylandı' :
                                          transfer.status === 'pending' ? 'Beklemede' :
                                            transfer.status === 'cancelled' ? 'İptal Edildi' :
                                              transfer.status === 'cancelled_by_customer' ? 'Müşteri İptali' :
                                                transfer.status === 'cancelled_by_operator' ? 'Operatör İptali' :
                                                  transfer.status === 'cancelled_by_driver' ? 'Şoför İptali' :
                                                    transfer.status === 'no_show' ? 'Gelmedi' :
                                                      transfer.status}
                            </span>
                            <div className="text-sm text-gray-500 mt-1">{transfer.vehicle}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Rezervasyon Raporu */}
            {activeTab === 'reservations' && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Rezervasyon Raporu</h2>

                {/* Genel Özet */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-blue-900">Toplam Rezervasyon</h3>
                    <p className="text-3xl font-bold text-blue-700">{reportData.summary?.totalReservations || 0}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-green-900">Toplam Yolcu</h3>
                    <p className="text-3xl font-bold text-green-700">{reportData.summary?.totalPassengers || 0}</p>
                  </div>
                </div>

                {/* Para Birimi Bazında Özet */}
                {reportData.summary?.byCurrency && Object.keys(reportData.summary.byCurrency).length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Para Birimi Bazında Özet</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {Object.entries(reportData.summary.byCurrency).map(([currency, data]) => (
                        <div key={currency} className="bg-white border rounded-lg p-4 shadow-sm">
                          <h4 className="font-medium text-gray-900 mb-3 text-center">{currency}</h4>
                          <div className="space-y-2">
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600">Rezervasyon:</span>
                              <span className="font-medium">{data.totalReservations}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600">Yolcu:</span>
                              <span className="font-medium">{data.totalPassengers}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-green-600">Toplam Gelir:</span>
                              <span className="font-medium text-green-600">{data.totalRevenue}</span>
                            </div>
                            <div className="flex justify-between items-center border-t pt-2">
                              <span className="text-sm font-medium text-purple-600">Ort. Gelir:</span>
                              <span className="font-bold text-purple-600">{data.averageRevenuePerReservation}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rezervasyon No</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transfer Tarihi</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Yön</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acente</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nereden</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nereye</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Yolcu Sayısı</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fiyat</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ödeme</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Durum</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {Array.isArray(reportData.reservations) ? reportData.reservations.map((reservation) => (
                        <tr key={reservation.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{reservation.reservationNumber}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{reservation.transferDates}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${reservation.isReturn ? 'bg-purple-100 text-purple-800' : 'bg-indigo-100 text-indigo-800'}`}>
                              {reservation.isReturn ? 'Dönüş' : 'Gidiş'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{reservation.agencyName}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{reservation.fromRegion}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{reservation.toRegion}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{reservation.passengerCount}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">{reservation.price} {reservation.currency}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${reservation.paymentStatus === 'paid' ? 'bg-green-100 text-green-800' :
                              reservation.paymentStatus === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                              {reservation.paymentStatus === 'paid' ? 'Ödendi' : reservation.paymentStatus === 'partial' ? 'Kısmi' : 'Bekliyor'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${reservation.status === 'completed' ? 'bg-green-100 text-green-800' :
                              reservation.status === 'confirmed' ? 'bg-blue-100 text-blue-800' :
                                reservation.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-red-100 text-red-800'
                              }`}>
                              {reservation.status}
                            </span>
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                            Rezervasyon verisi bulunamadı
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Transfer Operasyon Raporu */}
            {activeTab === 'operations' && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Transfer Operasyon Raporu</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-blue-900">Toplam Transfer</h3>
                    <p className="text-3xl font-bold text-blue-700">{reportData.summary?.totalTransfers || 0}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-green-900">Tamamlanan Transfer</h3>
                    <p className="text-3xl font-bold text-green-700">{reportData.summary?.completedTransfers || 0}</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-purple-900">Tamamlanma Oranı</h3>
                    <p className="text-3xl font-bold text-purple-700">{reportData.summary?.completionRate || '0%'}</p>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-yellow-900">İptal Oranı</h3>
                    <p className="text-3xl font-bold text-yellow-700">{reportData.summary?.cancellationRate || '0%'}</p>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transfer No</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tarih</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rota</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Araç</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Taşıyıcı</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acente</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Durum</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {Array.isArray(reportData.transfers) ? reportData.transfers.map((transfer) => (
                        <tr key={transfer.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{transfer.reservationNumber}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{transfer.transferDate}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{transfer.fromRegion} → {transfer.toRegion}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{transfer.vehicle}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{transfer.carrier}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{transfer.agencyName}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${transfer.status === 'completed' ? 'bg-green-100 text-green-800' :
                              transfer.status === 'passenger_on_board' ? 'bg-purple-100 text-purple-800' :
                                transfer.status === 'arrived_at_pickup' ? 'bg-orange-100 text-orange-800' :
                                  transfer.status === 'on_the_way_to_pickup' ? 'bg-yellow-100 text-yellow-800' :
                                    transfer.status === 'vehicle_assigned' ? 'bg-indigo-100 text-indigo-800' :
                                      transfer.status === 'confirmed' ? 'bg-blue-100 text-blue-800' :
                                        transfer.status === 'pending' ? 'bg-gray-100 text-gray-800' :
                                          'bg-red-100 text-red-800'
                              }`}>
                              {transfer.status}
                            </span>
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="7" className="px-6 py-4 text-center text-sm text-gray-500">
                            Transfer verisi bulunamadı
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Finansal Rapor */}
            {activeTab === 'financial' && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Finansal Rapor</h2>

                {/* Genel Özet */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-blue-900">Toplam İşlem</h3>
                    <p className="text-3xl font-bold text-blue-700">{reportData.summary?.totalTransactions || 0}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-green-900">Toplam Gelir</h3>
                    <p className="text-3xl font-bold text-green-700">{reportData.summary?.totalIncome || '€0'}</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-red-900">Toplam Gider</h3>
                    <p className="text-3xl font-bold text-red-700">{reportData.summary?.totalExpense || '€0'}</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-purple-900">Net Bakiye</h3>
                    <p className="text-3xl font-bold text-purple-700">{reportData.summary?.netBalance || '€0'}</p>
                  </div>
                </div>

                {/* Para Birimi Bazında Özet */}
                {reportData.summary?.byCurrency && Object.keys(reportData.summary.byCurrency).length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Para Birimi Bazında Özet</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {Object.entries(reportData.summary.byCurrency).map(([currency, data]) => (
                        <div key={currency} className="bg-white border rounded-lg p-4 shadow-sm">
                          <h4 className="font-medium text-gray-900 mb-3 text-center">{currency}</h4>
                          <div className="space-y-2">
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600">İşlem Sayısı:</span>
                              <span className="font-medium">{data.transactionCount}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-green-600">Gelir:</span>
                              <span className="font-medium text-green-600">{data.totalIncome}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-red-600">Gider:</span>
                              <span className="font-medium text-red-600">{data.totalExpense}</span>
                            </div>
                            <div className="flex justify-between items-center border-t pt-2">
                              <span className="text-sm font-medium text-purple-600">Net:</span>
                              <span className="font-bold text-purple-600">{data.netBalance}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tarih</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tür</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tutar</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Varlık</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Açıklama</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Oluşturan</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {Array.isArray(reportData.transactions) ? reportData.transactions.map((transaction) => (
                        <tr key={transaction.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{transaction.date}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${transaction.type === 'payment' ? 'bg-green-100 text-green-800' :
                              transaction.type === 'charge' ? 'bg-red-100 text-red-800' :
                                transaction.type === 'refund' ? 'bg-blue-100 text-blue-800' :
                                  'bg-yellow-100 text-yellow-800'
                              }`}>
                              {transaction.type}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{transaction.amount}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{transaction.entityName || '-'}</td>
                          <td className="px-6 py-4 text-sm text-gray-500">{transaction.description || '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{transaction.createdBy}</td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                            İşlem verisi bulunamadı
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Acente Performans Raporu */}
            {activeTab === 'agencies' && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Acente Performans Raporu</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-blue-900">Toplam Acente</h3>
                    <p className="text-3xl font-bold text-blue-700">{reportData.agencies?.length || 0}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-green-900">Toplam Rezervasyon</h3>
                    <p className="text-3xl font-bold text-green-700">
                      {reportData.agencies?.reduce((sum, agency) => sum + agency.summary.totalReservations, 0) || 0}
                    </p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-purple-900">Toplam Gelir</h3>
                    <p className="text-3xl font-bold text-purple-700">
                      {reportData.agencies?.reduce((sum, agency) => sum + parseFloat(agency.summary.totalRevenue.replace('€', '').replace(',', '')), 0).toFixed(2) || '0'} €
                    </p>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-yellow-900">Ortalama Tamamlanma</h3>
                    <p className="text-3xl font-bold text-yellow-700">
                      {reportData.agencies?.length > 0
                        ? (reportData.agencies.reduce((sum, agency) => sum + parseFloat(agency.summary.completedTransferRate.replace('%', '')), 0) / reportData.agencies.length).toFixed(2) + '%'
                        : '0%'}
                    </p>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acente</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rezervasyon</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Yolcu</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gelir</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tamamlanma</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">İptal</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {Array.isArray(reportData.agencies) ? reportData.agencies.map((agency) => (
                        <tr key={agency.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            <div>
                              <div>{agency.name}</div>
                              <div className="text-xs text-gray-500">{agency.email}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{agency.summary.totalReservations}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{agency.summary.totalPassengers}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{agency.summary.totalRevenue}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                              {agency.summary.completedTransferRate}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                              {agency.summary.cancellationRate}
                            </span>
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                            Acente verisi bulunamadı
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Taşıyıcı Performans Raporu */}
            {activeTab === 'carriers' && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Taşıyıcı Performans Raporu</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-blue-900">Toplam Taşıyıcı</h3>
                    <p className="text-3xl font-bold text-blue-700">{reportData.carriers?.length || 0}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-green-900">Toplam Transfer</h3>
                    <p className="text-3xl font-bold text-green-700">
                      {reportData.carriers?.reduce((sum, carrier) => sum + carrier.summary.totalTransfers, 0) || 0}
                    </p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-purple-900">Tamamlanan Transfer</h3>
                    <p className="text-3xl font-bold text-purple-700">
                      {reportData.carriers?.reduce((sum, carrier) => sum + carrier.summary.completedTransfers, 0) || 0}
                    </p>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-yellow-900">Ortalama Tamamlanma</h3>
                    <p className="text-3xl font-bold text-yellow-700">
                      {reportData.carriers?.length > 0
                        ? (reportData.carriers.reduce((sum, carrier) => sum + parseFloat(carrier.summary.completionRate.replace('%', '')), 0) / reportData.carriers.length).toFixed(2) + '%'
                        : '0%'}
                    </p>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Taşıyıcı</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Araç</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transfer</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tamamlanan</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tamamlanma</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">İptal</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {Array.isArray(reportData.carriers) ? reportData.carriers.map((carrier) => (
                        <tr key={carrier.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            <div>
                              <div>{carrier.name}</div>
                              <div className="text-xs text-gray-500">{carrier.email}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{carrier.vehicleCount}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{carrier.summary.totalTransfers}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{carrier.summary.completedTransfers}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                              {carrier.summary.completionRate}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                              {carrier.summary.cancellationRate}
                            </span>
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                            Taşıyıcı verisi bulunamadı
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Araç Kullanım Raporu */}
            {activeTab === 'vehicles' && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Araç Kullanım Raporu</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-blue-900">Toplam Araç</h3>
                    <p className="text-3xl font-bold text-blue-700">{reportData.vehicles?.length || 0}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-green-900">Toplam Transfer</h3>
                    <p className="text-3xl font-bold text-green-700">
                      {reportData.vehicles?.reduce((sum, vehicle) => sum + vehicle.summary.totalTransfers, 0) || 0}
                    </p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-purple-900">Tamamlanan Transfer</h3>
                    <p className="text-3xl font-bold text-purple-700">
                      {reportData.vehicles?.reduce((sum, vehicle) => sum + vehicle.summary.completedTransfers, 0) || 0}
                    </p>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-yellow-900">Ortalama Tamamlanma</h3>
                    <p className="text-3xl font-bold text-yellow-700">
                      {reportData.vehicles?.length > 0
                        ? (reportData.vehicles.reduce((sum, vehicle) => sum + parseFloat(vehicle.summary.completionRate.replace('%', '')), 0) / reportData.vehicles.length).toFixed(2) + '%'
                        : '0%'}
                    </p>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Araç</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tip</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Taşıyıcı</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transfer</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tamamlanma</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sürücü</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {Array.isArray(reportData.vehicles) ? reportData.vehicles.map((vehicle) => (
                        <tr key={vehicle.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            <div>
                              <div>{vehicle.plateNumber}</div>
                              <div className="text-xs text-gray-500">{vehicle.isActive ? 'Aktif' : 'Pasif'}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{vehicle.vehicleType}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{vehicle.carrier}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{vehicle.summary.totalTransfers}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                              {vehicle.summary.completionRate}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div>
                              <div>{vehicle.driverName || '-'}</div>
                              <div className="text-xs text-gray-500">{vehicle.driverPhone || '-'}</div>
                            </div>
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                            Araç verisi bulunamadı
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Bölge Raporu */}
            {activeTab === 'regions' && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Bölge Raporu</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-blue-900">Toplam Bölge</h3>
                    <p className="text-3xl font-bold text-blue-700">{reportData.regions?.length || 0}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-green-900">Toplam Rezervasyon</h3>
                    <p className="text-3xl font-bold text-green-700">
                      {reportData.regions?.reduce((sum, region) => sum + region.summary.totalReservations, 0) || 0}
                    </p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-purple-900">Giden Rezervasyon</h3>
                    <p className="text-3xl font-bold text-purple-700">
                      {reportData.regions?.reduce((sum, region) => sum + region.summary.totalFromReservations, 0) || 0}
                    </p>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-yellow-900">Gelen Rezervasyon</h3>
                    <p className="text-3xl font-bold text-yellow-700">
                      {reportData.regions?.reduce((sum, region) => sum + region.summary.totalToReservations, 0) || 0}
                    </p>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bölge</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kategori</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rezervasyon</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Giden</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gelen</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Yolcu</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {Array.isArray(reportData.regions) ? reportData.regions.map((region) => (
                        <tr key={region.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            <div>
                              <div>{region.name}</div>
                              <div className="text-xs text-gray-500">{region.parentRegion ? 'Alt Bölge' : 'Ana Bölge'}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{region.category}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{region.summary.totalReservations}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{region.summary.totalFromReservations}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{region.summary.totalToReservations}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{region.summary.totalPassengers}</td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                            Bölge verisi bulunamadı
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12">
            <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Rapor Verisi Yok</h3>
            <p className="mt-1 text-sm text-gray-500">
              Raporu görüntülemek için "Raporu Getir" butonuna tıklayın.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports;